/**
 * Rendered period array for the merged table's availability columns — split
 * out of `use-offer-availability-query.ts` to keep that hook under the
 * max-lines-per-function budget.
 *
 * Covers the offer's own `defaultDateFrom`..`defaultDateTo` range exactly, at
 * whatever granularity the caller has chosen — no rolling "today"-anchored
 * horizon, no window/pagination state (CU-86cbejhgd removed both as a
 * regression). Reuses `buildPeriods` (the catalog's own period builder, same
 * id/label convention e2e fixtures and other consumers already rely on)
 * driven by the offer's real start date and a horizon computed to cover its
 * real end date, instead of "today" and a capped 3-12 month window, then
 * clamps the result to the exact range with `clampToRange` — `buildPeriods`
 * always rounds out to whole calendar months, so a mid-month campaign would
 * otherwise render days/weeks/half-months outside the offer's real dates.
 */
import * as React from 'react';
import { buildPeriods, type Granularity, type InventoryAvailabilityPeriod } from '@ooh/screens';

/** Whole calendar months from `from`'s month through `to`'s month, inclusive. */
function countMonthsInclusive(from: string, to: string): number {
  const fromDate = new Date(`${from}T00:00:00.000Z`);
  const toDate = new Date(`${to}T00:00:00.000Z`);
  return (
    (toDate.getUTCFullYear() - fromDate.getUTCFullYear()) * 12 +
    (toDate.getUTCMonth() - fromDate.getUTCMonth()) +
    1
  );
}

/**
 * Drops periods entirely outside `[from, to]` and clips the boundary periods'
 * start/end to it — `buildPeriods` always rounds its rendered span out to
 * whole calendar months, so a mid-month campaign would otherwise render
 * columns for days/weeks before `from` or after `to` that fall outside the
 * offer's real range. ISO `YYYY-MM-DD` strings sort lexically identical to
 * chronological order, so plain string comparison is sufficient here. A
 * period whose own id/label already matches `buildPeriods`'s convention is
 * left unchanged when it doesn't cross a boundary (the common, e2e-tested
 * case: `defaultDateFrom`/`defaultDateTo` land on a granularity boundary).
 */
function clampToRange(
  periods: InventoryAvailabilityPeriod[],
  from: string,
  to: string,
): InventoryAvailabilityPeriod[] {
  return periods
    .filter((period) => period.end >= from && period.start <= to)
    .map((period) => ({
      ...period,
      start: period.start < from ? from : period.start,
      end: period.end > to ? to : period.end,
    }));
}

export interface UseOfferAvailabilityPeriodsOptions {
  effectiveShowAvailability: boolean;
  defaultDateFrom: string | null;
  defaultDateTo: string | null;
  granularity: Granularity;
}

export function useOfferAvailabilityPeriods({
  effectiveShowAvailability,
  defaultDateFrom,
  defaultDateTo,
  granularity,
}: UseOfferAvailabilityPeriodsOptions): InventoryAvailabilityPeriod[] {
  return React.useMemo(() => {
    if (!effectiveShowAvailability || !defaultDateFrom || !defaultDateTo) return [];
    const horizonMonths = countMonthsInclusive(defaultDateFrom, defaultDateTo);
    const periods = buildPeriods(
      granularity,
      horizonMonths,
      new Date(`${defaultDateFrom}T00:00:00.000Z`),
    );
    return clampToRange(periods, defaultDateFrom, defaultDateTo);
  }, [effectiveShowAvailability, defaultDateFrom, defaultDateTo, granularity]);
}
